/**
 * F&O Positions Tab
 *
 * Full list view for F&O Positions with client-side filtering and bulk
 * reassignment to a target F&O Job. Mirrors the FoJobsTab architecture.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users, Search, RefreshCw, ArrowRight, Loader2, X, Filter, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { jobFamilySyncService, FoPositionListItem } from '@/services/api/organizationService';
import ReassignPositionsToJobDialog from './ReassignPositionsToJobDialog';
import PositionRetirement from './PositionRetirement';

const PAGE_SIZE = 50;

type WorkerFilter = 'all' | 'assigned' | 'vacant' | 'retired';

const FoPositionsTab: React.FC = () => {
  const [positions, setPositions] = useState<FoPositionListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [jobFilter, setJobFilter] = useState<string>('all');
  const [jobFamilyFilter, setJobFamilyFilter] = useState<string>('all');
  const [workerFilter, setWorkerFilter] = useState<WorkerFilter>('all');
  const [page, setPage] = useState(0);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reassign dialog
  const [reassignOpen, setReassignOpen] = useState(false);

  // List vs stale-position retirement (bulk) view.
  const [view, setView] = useState<'list' | 'retirement'>('list');

  // ── Load data ───────────────────────────────────────────────────────────

  const loadData = useCallback(async (refresh: boolean = false) => {
    setIsLoading(true);
    try {
      const data = await jobFamilySyncService.listFoPositions(refresh);
      setPositions(data.items);
    } catch (e) {
      console.error('Failed to load F&O positions:', e);
      setSyncMessage('Failed to load F&O positions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await jobFamilySyncService.exportFoPositions();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fo_positions_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Positions export failed', e);
    } finally {
      setExporting(false);
    }
  };

  // ── Derived lookups for filter dropdowns ───────────────────────────────

  const jobOptions = useMemo(() => {
    const map = new Map<string, number>();
    positions.forEach(p => {
      const jid = (p.job_id || '').trim();
      if (jid) map.set(jid, (map.get(jid) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [positions]);

  const jobFamilies = useMemo(() => {
    const set = new Set<string>();
    positions.forEach(p => { if (p.job_family) set.add(p.job_family); });
    return Array.from(set).sort();
  }, [positions]);

  // ── Client-side filtering ──────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = positions;

    if (jobFilter !== 'all') {
      list = list.filter(p => (p.job_id || '').trim() === jobFilter);
    }
    if (jobFamilyFilter !== 'all') {
      list = list.filter(p => p.job_family === jobFamilyFilter);
    }
    if (workerFilter === 'retired') {
      list = list.filter(p => !!p.is_retired);
    } else {
      // Retired seats are out of the live establishment — hide them from All / Assigned / Vacant.
      list = list.filter(p => !p.is_retired);
      if (workerFilter === 'assigned') {
        list = list.filter(p => !!p.worker_personnel_number);
      } else if (workerFilter === 'vacant') {
        list = list.filter(p => !p.worker_personnel_number);
      }
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(p =>
        p.position_id.toLowerCase().includes(s) ||
        (p.worker_name || '').toLowerCase().includes(s) ||
        (p.worker_personnel_number || '').toLowerCase().includes(s) ||
        (p.title_id || '').toLowerCase().includes(s) ||
        (p.job_id || '').toLowerCase().includes(s)
      );
    }

    return list;
  }, [positions, jobFilter, jobFamilyFilter, workerFilter, search]);

  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
  }, [search, jobFilter, jobFamilyFilter, workerFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedPositions = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ── Summary stats ───────────────────────────────────────────────────────

  const stats = useMemo(() => {
    // Stats reflect the LIVE establishment — retired seats are excluded (they still
    // exist in F&O but are abolished here), and surfaced via the Retired filter.
    const live = positions.filter(p => !p.is_retired);
    const total = live.length;
    const assigned = live.filter(p => !!p.worker_personnel_number).length;
    const vacant = total - assigned;
    const primary = live.filter(p => p.is_primary_position === true).length;
    const retired = positions.length - live.length;
    return { total, assigned, vacant, primary, retired };
  }, [positions]);

  // ── Selection helpers ──────────────────────────────────────────────────

  const toggleSelected = (pid: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const togglePageAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = pagedPositions.every(p => next.has(p.position_id));
      if (allSelected) {
        pagedPositions.forEach(p => next.delete(p.position_id));
      } else {
        pagedPositions.forEach(p => next.add(p.position_id));
      }
      return next;
    });
  };

  const pageAllSelected = pagedPositions.length > 0 && pagedPositions.every(p => selectedIds.has(p.position_id));

  const selectedPositions = useMemo(
    () => positions.filter(p => selectedIds.has(p.position_id)),
    [positions, selectedIds]
  );

  // ── Filter active count ─────────────────────────────────────────────────

  const activeFilters =
    (jobFilter !== 'all' ? 1 : 0) +
    (jobFamilyFilter !== 'all' ? 1 : 0) +
    (workerFilter !== 'all' ? 1 : 0);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-heritage-blue">{stats.total}</div>
            <div className="text-xs text-gray-500">Total Positions</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-emerald-600">{stats.assigned}</div>
            <div className="text-xs text-gray-500">Assigned</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-amber-600">{stats.vacant}</div>
            <div className="text-xs text-gray-500">Vacant</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-blue-600">{stats.primary}</div>
            <div className="text-xs text-gray-500">Primary</div>
          </CardContent>
        </Card>
      </div>

      {/* View toggle: full position list vs stale-position retirement (bulk) */}
      <div className="flex items-center gap-2">
        <button onClick={() => setView('list')}
          className={cn('rounded-full border px-3 py-1 text-xs', view === 'list' ? 'border-heritage-blue bg-heritage-blue text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
          All positions
        </button>
        <button onClick={() => setView('retirement')}
          className={cn('rounded-full border px-3 py-1 text-xs', view === 'retirement' ? 'border-heritage-blue bg-heritage-blue text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
          Stale retirement
        </button>
      </div>

      {view === 'retirement' && <PositionRetirement />}

      {view === 'list' && (<>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search position, worker, title..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={jobFilter} onValueChange={setJobFilter}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Job" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Jobs</SelectItem>
            {jobOptions.map(([jid, count]) => (
              <SelectItem key={jid} value={jid}>
                {jid} ({count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={jobFamilyFilter} onValueChange={setJobFamilyFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Job Family" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Families</SelectItem>
            {jobFamilies.map(f => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={workerFilter} onValueChange={(v) => setWorkerFilter(v as WorkerFilter)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Occupancy" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="vacant">Vacant</SelectItem>
            <SelectItem value="retired">Retired{stats.retired ? ` (${stats.retired})` : ''}</SelectItem>
          </SelectContent>
        </Select>

        {activeFilters > 0 && (
          <Button
            variant="ghost" size="sm"
            className="text-xs text-gray-500"
            onClick={() => {
              setJobFilter('all'); setJobFamilyFilter('all'); setWorkerFilter('all');
            }}
          >
            <X className="h-3 w-3 mr-1" /> Clear ({activeFilters})
          </Button>
        )}

        <div className="flex-1" />

        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
          {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Export
        </Button>
        <Button variant="outline" size="sm" onClick={() => loadData(true)} disabled={isLoading}>
          <RefreshCw className={cn('h-4 w-4 mr-1', isLoading && 'animate-spin')} /> Refresh
        </Button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-heritage-blue/5 border border-heritage-blue/20 rounded-md">
          <div className="text-sm text-gray-700">
            <strong>{selectedIds.size}</strong> selected
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline"
              className="h-8 text-xs"
              onClick={() => setReassignOpen(true)}
            >
              <ArrowRight className="h-3 w-3 mr-1" /> Reassign to Job
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-8 text-xs text-gray-500"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {syncMessage && (
        <div className="text-sm text-gray-600 bg-gray-50 border rounded px-3 py-2 flex items-center justify-between">
          <span>{syncMessage}</span>
          <Button variant="ghost" size="sm" onClick={() => setSyncMessage(null)}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Table */}
      {isLoading && positions.length === 0 ? (
        <div className="py-12 text-center text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading F&O Positions (may take up to 10s on cold cache)...
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">
          {positions.length === 0 ? 'No F&O Positions found.' : 'No positions match current filters.'}
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80">
                  <TableHead className="w-8">
                    <Checkbox checked={pageAllSelected} onCheckedChange={togglePageAll} />
                  </TableHead>
                  <TableHead className="text-xs font-medium">Position ID</TableHead>
                  <TableHead className="text-xs font-medium">Worker</TableHead>
                  <TableHead className="text-xs font-medium">Title</TableHead>
                  <TableHead className="text-xs font-medium">Job</TableHead>
                  <TableHead className="text-xs font-medium">Job Family</TableHead>
                  <TableHead className="text-xs font-medium">Dept</TableHead>
                  <TableHead className="text-xs font-medium">Campus</TableHead>
                  <TableHead className="text-xs font-medium">Dim Dept</TableHead>
                  <TableHead className="text-xs font-medium">Faculty</TableHead>
                  <TableHead className="text-xs font-medium text-center w-[60px]">FTE</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedPositions.map(p => {
                  const isSelected = selectedIds.has(p.position_id);
                  return (
                    <TableRow
                      key={p.position_id}
                      className={cn(
                        'text-sm cursor-pointer hover:bg-gray-50',
                        isSelected && 'bg-heritage-blue/5',
                      )}
                      onClick={() => toggleSelected(p.position_id)}
                    >
                      <TableCell className="w-8">
                        <Checkbox checked={isSelected} />
                      </TableCell>
                      <TableCell className="font-mono text-xs whitespace-nowrap">{p.position_id}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {p.worker_name ? (
                          <span>
                            {p.worker_name}
                            {p.worker_personnel_number && (
                              <span className="text-gray-400 ml-1">({p.worker_personnel_number})</span>
                            )}
                          </span>
                        ) : p.is_retired ? (
                          <Badge variant="outline" className="text-[10px] border-red-200 text-red-500">Retired</Badge>
                        ) : (
                          <span className="text-gray-400 italic">Vacant</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {p.title_id || <span className="text-gray-300 italic">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                        {p.job_id || <span className="text-gray-300">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                        {p.job_family || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                        {p.department_number || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 font-mono whitespace-nowrap">
                        {p.dimension_campus || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 font-mono whitespace-nowrap">
                        {p.dimension_department || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 font-mono whitespace-nowrap">
                        {p.dimension_faculty || '—'}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {p.full_time_equivalent ?? '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-gray-500">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                  Previous
                </Button>
                <span className="text-xs text-gray-500 self-center px-2">
                  Page {page + 1} of {pageCount}
                </span>
                <Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      </>)}

      {/* Reassign dialog */}
      {reassignOpen && selectedPositions.length > 0 && (
        <ReassignPositionsToJobDialog
          open={reassignOpen}
          onOpenChange={setReassignOpen}
          preSelectedPositions={selectedPositions}
          onComplete={() => {
            setReassignOpen(false);
            setSelectedIds(new Set());
            loadData(true);
            setSyncMessage('Reassignment complete');
          }}
        />
      )}
    </div>
  );
};

export default FoPositionsTab;
